import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  DASHBOARDING_ONBOARDING_CARD_VISIBLE,
  DASHBOARDING_ONBOARDING_LOADED,
} from 'constants/events';
import { ORG_ROLE_ADMIN } from 'constants/organizationRoles';
import { CUSTOM_BRANDING_ACCESS } from 'constants/scopes';
import {
  EXPLORE_TEAM_LIBRARY_CARD_KEY,
  WORKSPACE_ELEMENT_SHOWN,
} from 'constants/workspaceOnboarding';
import { PRIVATE_WORKSPACE, TEAM_WORKSPACE } from 'constants/workspaces';
import * as analytics from 'utilities/analytics';

import { hasScope } from 'selectors/scopes';

import {
  useGetSelectedWorkspace,
  useGetUserRoleForSelectedWorkspace,
  useGetWorkspaceLibraryVideoCounts,
  useGetWorkspaceRecentTeamVideos,
} from './workspace';
import {
  dismissElementHandler,
  getDashboardOnboardingCards,
  markEligibleElementAsCompleteForSelectedWorkspace,
} from '../components/videos-dashboard-ta/dashboard-onboarding/utilities';

import { getDashboardOnboardingCards as getDashboardOnboardingCardsTa } from 'components/videos-dashboard-ta/dashboard-onboarding-ta/utilities';
import { COMPACT_ONBOARDING_INTRO_CARDS } from 'components/videos-dashboard-ta/dashboard-onboarding/constants';

export const useGetSelectedWorkspaceOnboarding = () => {
  const [cards, setCards] = useState();

  const selectedWorkspace = useGetSelectedWorkspace();

  useEffect(() => {
    if (!selectedWorkspace) {
      return;
    }

    setCards(selectedWorkspace.onboarding);
  }, [selectedWorkspace]);

  return cards;
};

export function useGetUserOnboardingPersonaInfo() {
  const useCasePlan = useSelector(state => state.welcome.use_case_plan);
  const useCasePlanPersona = useSelector(
    state => state.welcome.use_case_plan_persona
  );
  const companySize = useSelector(state => state.welcome.company_size);
  const companyName = useSelector(state => state.welcome.company);
  const companyRole = useSelector(state => state.welcome.role);
  const companyCustomRole = useSelector(state => state.welcome.custom_role);

  const sharingIntent = useSelector(state => state.welcome.sharing_intent_case);
  const sharingIntentExplanation = useSelector(
    state => state.welcome.other_intent_case_explanation
  );

  return {
    use_case_plan: useCasePlan,
    use_case_plan_persona: useCasePlanPersona,
    company_size: companySize,
    company: companyName,
    role: companyRole,
    custom_role: companyCustomRole,
    sharing_intent: sharingIntent,
    other_sharing_intent_explanation: sharingIntentExplanation,
  };
}

// if the intro cards are the last cards left, auto dismiss them
export function useHandleLastIntroCardDismissal(cards = []) {
  const dispatch = useDispatch();
  const selectedWorkspace = useGetSelectedWorkspace();
  const [updating, setUpdating] = useState(false);

  const introCardOnly =
    cards?.length === 1 && COMPACT_ONBOARDING_INTRO_CARDS.has(cards[0].key);

  const cardKey = introCardOnly && cards[0].key;
  const selectedWorkspaceId = selectedWorkspace?.id;

  useEffect(() => {
    if (!introCardOnly || updating || !selectedWorkspaceId || !cardKey) {
      return;
    }

    setUpdating(true);

    dismissElementHandler(dispatch, cardKey, selectedWorkspaceId);
  }, [cardKey, dispatch, introCardOnly, updating, selectedWorkspaceId]);

  return introCardOnly;
}

export function useHandleTeamLibraryVisit() {
  const dispatch = useDispatch();
  const library = useSelector(state => state.videos_dashboard?.workspace);
  const selectedWorkspace = useGetSelectedWorkspace();

  useEffect(() => {
    if (library !== TEAM_WORKSPACE || !selectedWorkspace) {
      return;
    }

    markEligibleElementAsCompleteForSelectedWorkspace(
      dispatch,
      [selectedWorkspace],
      EXPLORE_TEAM_LIBRARY_CARD_KEY
    );
  }, [dispatch, library, selectedWorkspace]);
}

export function useGetDashboardOnboardingCards(
  library,
  showCards,
  oldState = true
) {
  const onboardingState = useGetSelectedWorkspaceOnboarding();

  const userRole = useGetUserRoleForSelectedWorkspace();

  const libraryVideoCounts = useGetWorkspaceLibraryVideoCounts();

  const userCanCustomizeSharePage = useSelector(state =>
    hasScope(state, CUSTOM_BRANDING_ACCESS)
  );

  // Mark relevant cards as complete on team library visit
  useHandleTeamLibraryVisit();

  const [loaded, setLoaded] = useState(false);

  const skipCheckForTeamVideos =
    !showCards || (library !== PRIVATE_WORKSPACE && library !== TEAM_WORKSPACE);

  // Fetch recent team videos data only when necessary
  const teamLibraryVideos = useGetWorkspaceRecentTeamVideos(
    skipCheckForTeamVideos
  );
  const privateLibraryVideos = libraryVideoCounts?.[PRIVATE_WORKSPACE];

  const dataReady =
    onboardingState &&
    userRole &&
    libraryVideoCounts &&
    (!skipCheckForTeamVideos || teamLibraryVideos !== null);

  const isAdmin = userRole === ORG_ROLE_ADMIN;

  const getCardsFn = oldState
    ? getDashboardOnboardingCards
    : getDashboardOnboardingCardsTa;

  const cards = dataReady
    ? getCardsFn({
        library,
        isAdmin,
        onboardingState,
        userCanCustomizeSharePage,
        teamLibraryVideos,
        privateLibraryVideos,
      })
    : [];

  // Log analytics
  useEffect(() => {
    if (loaded || cards.length === 0 || !dataReady) {
      return;
    }

    const cardDetails = cards
      .map(card => card.key)
      .reduce(
        (obj, cardKey) => ({
          ...obj,
          [cardKey]: onboardingState[cardKey],
        }),
        {}
      );

    analytics.track(DASHBOARDING_ONBOARDING_LOADED, {
      cardDetails,
      isAdmin,
      library,
      showCards,
    });

    setLoaded(true);
  }, [cards, dataReady, isAdmin, library, loaded, onboardingState, showCards]);

  const incompleteCardCount = cards.reduce((acc, card) => {
    return onboardingState[card.key] === WORKSPACE_ELEMENT_SHOWN
      ? acc + 1
      : acc;
  }, 0);

  return { cards, incompleteCardCount };
}

export function useTrackAnalyticsForVisibleCards(
  sliderRef,
  cards,
  workspaceId
) {
  const innerSliderRef = sliderRef?.current?.innerSlider;

  const innerSliderShown = innerSliderRef?.props.slidesToShow;
  const innerSliderSelected = innerSliderRef?.state.currentSlide;

  const cardKeys = cards.map(card => card.key);

  useEffect(() => {
    if (cardKeys.length === 0 || !sliderRef.current) {
      return;
    }

    const lastIndex = innerSliderSelected + innerSliderShown;

    let cardsShown = [];

    if (lastIndex > cardKeys.length - 1) {
      cardsShown = cardKeys.slice(cardKeys.length - innerSliderShown);
    } else {
      cardsShown = cardKeys.slice(innerSliderSelected, lastIndex);
    }

    cardsShown.forEach(card => {
      analytics.track(DASHBOARDING_ONBOARDING_CARD_VISIBLE, {
        cardKey: card,
        workspaceId,
      });
    });
    // we only want to update analytics when the cards change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerSliderSelected, innerSliderShown]);
}
